import { Button, Flex, Text, Textarea, useToast } from "@chakra-ui/react";
import moment from "moment";
import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ContractTaskCommentDto,
  UpdateContractTaskCommentRequest,
  useDeleteContractTaskCommentMutation,
  useUpdateContractTaskCommentMutation,
} from "../../../../../../autogen/bff-api";
import { useLoggedInWithOrgContextState } from "../../../../../../common/auth/useLoggedInWithOrgContextState";
import { useCreateEditor, useSaveNow } from "../../../../../../common/editing/save-coordinator";
import { useApiError } from "../../../../../../common/errors/useApiError";
import { displayDateWithTime } from "../../../../../../common/formatting/displayDateWithTime";
import { Explanation } from "../../../../../../common/support/Explanation/Explanation";
import { displayPersonName } from "../../../../../../common/persons/displayPersonName";
import { CommentContent } from "./CommentContent";
import { RemoveCommentButton } from "./RemoveCommentButton";

interface Props {
  comment: ContractTaskCommentDto;
  onChangeComplete: () => void;
}

export const Comment: React.FC<Props> = ({ comment, onChangeComplete }) => {
  const [showEditing, setShowEditing] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [text, setText] = useState(comment.text);
  const { t } = useTranslation();
  const [deleteComment] = useDeleteContractTaskCommentMutation();
  const [editComment] = useUpdateContractTaskCommentMutation();
  const editorId = `contract-task-comment:${comment.id}`;
  useCreateEditor<ContractTaskCommentDto, UpdateContractTaskCommentRequest>(editorId, {
    initialState: comment,
    save: (fields) =>
      editComment({
        commentId: comment.id,
        updateContractTaskCommentRequest: fields as UpdateContractTaskCommentRequest,
      }).unwrap(),
  });
  const { saveNow } = useSaveNow<ContractTaskCommentDto, UpdateContractTaskCommentRequest>(editorId);
  const toast = useToast();
  const displayer = useApiError();
  const authState = useLoggedInWithOrgContextState();

  const createdByMe = authState.me.id === comment.createdBy.id;

  return (
    <Flex flexDirection={"column"} mt="15px">
      <Flex>
        <Text fontWeight={"semibold"} fontSize="md" color={createdByMe ? "blue.500" : "smTaskActivityText"}>
          {createdByMe ? t("You") : displayPersonName(comment.createdBy)}
        </Text>
        <Explanation enabled={true} text={displayDateWithTime(comment.createdAt, t("at"))}>
          <Text fontSize="sm" color={createdByMe ? "blue.500" : "smTaskActivityText"} ml="15px" mt="2px">
            {moment(comment.createdAt).fromNow()}
          </Text>
        </Explanation>
      </Flex>
      <Flex mt="5px">
        {showEditing ? (
          <Flex flexDirection={"column"} w="100%">
            <Textarea
              w="100%"
              value={text ?? undefined}
              onChange={(e) => setText(e.target.value)}
              placeholder={t("Edit comment") ?? ""}
            />
            <Flex flexDirection={"column"}>
              <Flex mt="5px">
                <Button
                  id="save-comment-button"
                  variant={"solid"}
                  colorScheme="teal"
                  size={"sm"}
                  mr="5px"
                  isLoading={isEditing}
                  onClick={async () => {
                    if (!text) {
                      throw new Error("Should never happen");
                    }
                    setIsEditing(true);
                    try {
                      await saveNow({ text: text });
                      setIsEditing(false);
                      onChangeComplete();
                      setShowEditing(false);
                    } catch (e) {
                      setIsEditing(false);
                      toast({
                        title: t("Could not edit comment"),
                        description: displayer.message(e),
                        status: "error",
                      });
                    }
                  }}
                >
                  {t("Save")}
                </Button>
                <Button
                  variant={"solid"}
                  colorScheme="gray"
                  size={"sm"}
                  isDisabled={isEditing}
                  onClick={() => setShowEditing(false)}
                >
                  {t("Cancel")}
                </Button>
              </Flex>
            </Flex>
          </Flex>
        ) : (
          <CommentContent comment={comment} />
        )}
      </Flex>
      {comment.canUpdate && !comment.deletedAt && (
        <Flex mt="10px">
          <Button id="edit-comment-button" variant="link" size="sm" ml="-2px" onClick={() => setShowEditing(true)}>
            {t("Edit")}
          </Button>
          <RemoveCommentButton
            confirmRemoval={async () => {
              try {
                await deleteComment({
                  commentId: comment.id,
                }).unwrap();
                onChangeComplete();
              } catch (e) {
                toast({
                  title: t("Could not delete comment"),
                  description: displayer.message(e),
                  status: "error",
                });
              }
            }}
          />
        </Flex>
      )}
    </Flex>
  );
};
